import React, { memo, useRef, useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getFromApiAsync } from '../../helpers/apiHelper';
import { useApiCategoryStock } from '../../helpers/apiCategoryStock';
import { formatPrice } from '../../helpers/utils';


const upColor = '#54B770', downColor = '#E46161', textColor = '#D6D8DA';

export default memo(function StockCharBarDetail(props) {
  const { code, type, chartType } = props;

  const { t } = useTranslation();
  const language = useSelector(state => state.gShared.language);
  const tradingDate = useSelector(state => state.gShared.tradingDate ?? '');

  //
  const refChart = useRef(null);
  const refFlagCheck = useRef(null);


  //
  const [isLoading, setIsLoading] = useState(false);

  //Lấy thông tin Lịch sử KL -> dùng cho Biểu đồ KL theo bước giá
  const [stockMatches, setStockMatches] = useState([]);
  const [chartData, setChartData] = useState({
    matchPrice: [],
    matchQtty: []
  });
  const refFirstTicks = useRef("0");
  const refLastTicks = useRef("0");
  const refCode = useRef(null);
  const _getMatchDetails = (searchCode, backward) => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    let _url =
      "pr/stockmatch/" + searchCode + "?lastTicks=" + refLastTicks.current;
    if (backward && backward === "Y") {
      _url =
        "pr/stockmatch/" +
        searchCode +
        "?lastTicks=" +
        refFirstTicks.current +
        "&backward=Y";
    }
    //
    getFromApiAsync(_url)
      .then((res) => {
        if (searchCode === refCode.current) {
          try {
            if (res.data && res.data.length > 0) {
              setStockMatches((prevState) =>
                [...prevState, ...res.data].sort((a, b) =>
                  b.ticks.localeCompare(a.ticks)
                )
              );
              //
              const _firstTicks = res.data.sort((a, b) =>
                a.ticks.localeCompare(b.ticks)
              )[0].ticks;
              const _lastTicks = res.data.sort((a, b) =>
                b.ticks.localeCompare(a.ticks)
              )[0].ticks;

              if (
                (refLastTicks.current ?? "0") === "0" ||
                _lastTicks.localeCompare(refLastTicks.current) > 0
              ) {
                refLastTicks.current = _lastTicks;
              }

              if (
                (refFirstTicks.current ?? "0") === "0" ||
                _firstTicks.localeCompare(refFirstTicks.current) < 0
              ) {
                refFirstTicks.current = _firstTicks;
              }
            }
          } catch (error) {
            console.log("error =>", error);
          }
          setIsLoading(false);
        } else {
          console.log(searchCode, refCode.current);
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.log("catch =>", err);
      });
  };

  //dữ liệu cho Biểu đồ khớp lệnh theo bước giá
  useEffect(() => {
    refCode.current = code;
    if (stockMatches && stockMatches.length > 0) {
      const tempChart = stockMatches.reduce((acc, curr) => {
        const groupKey = curr.matchPrice;
        if (!acc[groupKey]) {
          acc[groupKey] = 0;
        }
        acc[groupKey] += curr.matchQtty / 10;
        return acc;
      }, {});

      const _matchPriceArray = Object.keys(tempChart).map(price => formatPrice(price));
      const _matchQttyArray = Object.values(tempChart);

      setChartData(prevState => ({
        ...prevState,
        matchPrice: _matchPriceArray,
        matchQtty: _matchQttyArray
      }));
    }
  }, [code, stockMatches]);


  useEffect(() => {
    const interval = setInterval(() => {
      _getMatchDetails(code, "Y");
    }, 1000);

    // Hủy interval khi component bị unmount
    return () => clearInterval(interval);
  }, [code]);

  //
  const _getChartOptions = () => {
    return {
      tooltip: {},
      color: ['rgb(124, 181, 236)'],
      borderColor: ['red'],
      xAxis: {
        type: 'value',
        axisTick: { // Ẩn hiển thị các mốc của trục x
          show: true
        },
        axisLabel: { // Ẩn hiển thị chữ số trên trục x
          show: false
        }
      },
      yAxis: {
        type: 'category',
        // data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        data: chartData.matchPrice ?? [],
        axisLine: {
          lineStyle: {
            color: '#666666', // Màu của đường trục y
            fonstSize: 11,
          }
        }
      },
      series: [
        {
          name: 'MatchQtty',
          // data: [120, 200, 150, 80, 70, 110, 130],
          data: chartData.matchQtty ?? [],
          type: 'bar',
          itemStyle: {
            // Định dạng cho các cột
            borderColor: 'white', // Màu viền của cột
            borderWidth: 2, // Độ dày của viền (tuỳ chọn)
          },
          label: {
            show: true, // Hiển thị giá trị của cột
            position: 'inside', // Vị trí hiển thị giá trị, ví dụ: 'top', 'inside', 'insideTop', 'insideBottom', 'bottom', 'insideLeft', 'insideRight', ...
            textBorderColor: '#fff', // Màu của viền
            textBorderWidth: 2, // Độ dày của viền
            fontSize: 13,
            fontWeight: '550',
          }
        }
      ]
    }
  }

  const _handleWindowResize = () => {
    if (refChart && refChart.current) {
      refChart.current.getEchartsInstance().resize();
    }
  }

  useEffect(() => {
    if (refChart && refChart.current) {
      refChart.current.getEchartsInstance().setOption(_getChartOptions());
    }
  });


  useEffect(() => {
    window.addEventListener('resize', _handleWindowResize);
    return () => {
      window.removeEventListener('resize', _handleWindowResize);
    };
  }, [])

  //
  return (
    <>
      {chartData && chartData.matchPrice && chartData.matchPrice.length > 0 && <ReactECharts style={{ width: '100%', height: '100%', ...props.style }}
        ref={refChart}
        lazyUpdate={true}
        option={_getChartOptions()}
      />}
    </>
  )
})